import { CheckoutItem, CheckoutProduct, Discount } from '../models/APIModels';

/**
 * Builds the GTM ecommerce payloads for the checkout step.
 *
 * Every line the booking flow reports — a suitcase, a box, the storage
 * roll-up — is the same two shapes with the same four constants, and
 * `Payment.tsx` was writing both shapes out by hand in each branch. Three
 * copies of `brand: 'Kit Keeper'` is three chances for one of them to drift
 * and quietly split a report in the analytics dashboard.
 */

const BRAND = 'Kit Keeper';
const AFFILIATION = 'Online Store';
const CURRENCY = 'GBP';

export interface CheckoutLineInput {
  /** Absent for synthetic lines such as the storage roll-up. */
  id?: number;
  name: string;
  /** Pence, as everything else in this codebase stores money. */
  pricePence: number;
  category: string;
  index: number;
}

export interface CheckoutLine {
  product: CheckoutProduct;
  item: CheckoutItem;
}

function checkoutLine(
  { id, name, pricePence, category, index }: CheckoutLineInput,
  discount?: Discount,
): CheckoutLine {
  const price = (pricePence / 100).toFixed(2);

  return {
    product: {
      name,
      // Omitted rather than set undefined: the storage roll-up is a
      // synthetic line with no product id, and an explicit `undefined`
      // key is not the same payload as an absent one.
      ...(id !== undefined && { id }),
      price,
      brand: BRAND,
      category,
      quantity: 1,
      affiliation: AFFILIATION,
    },
    item: {
      ...(id !== undefined && { item_id: id.toString() }),
      item_name: name,
      affiliation: AFFILIATION,
      currency: CURRENCY,
      index,
      item_brand: BRAND,
      item_category: category,
      price,
      quantity: '1',
      ...(discount && {
        coupon: discount.discountCode,
        discount: `${discount.discountPercentage}%`,
      }),
    },
  };
}

/**
 * Splits a set of lines into the two parallel arrays GTM expects — the
 * legacy `eec_*` product list and the GA4 `items` list.
 */
export function buildCheckoutLines(
  lines: CheckoutLineInput[],
  discount?: Discount,
): { products: CheckoutProduct[]; items: CheckoutItem[] } {
  const built = lines.map((line) => checkoutLine(line, discount));

  return {
    products: built.map((line) => line.product),
    items: built.map((line) => line.item),
  };
}
